import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import PropTypes from 'prop-types';
import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import styled, { css, useTheme } from 'styled-components';

import { Button } from '@ui/buttons/Button.tsx';
import { Icon } from '@ui/icons/Icon.tsx';
import SearchInputLabelledMulti from '@ui/inputs/SearchInputLabelledMulti.tsx';
import Tooltip from '@ui/tooltip';

import { useCSSColors } from '../../../../../src/hooks/useCSSColors.ts';
import { usePillDrag } from '../../../../../src/hooks/usePillDrag.ts';
import { getHeaderPadding } from '../../../../utils/theming.ts';
import { MAX_SEARCH_INPUT_LENGTH } from '../constants.ts';
import AddWaypointButton from '../DirectionsControls/AddWaypointButton.tsx';
import LogoAndCloseButton from '../DirectionsControls/LogoAndCloseButton.tsx';
import {
  handleAddStop,
  handleDragStart,
  handleDragEnd,
  handleDragOver,
  handleRemoveClick,
  reverseDirections,
  handleClearButtonClick,
  handleMoveClick,
} from '../helpers/handleInputChangeEvents.ts';
import WheelchairAccessibilityToggle from '../helpers/WheelchairAccessibilityToggle.tsx';
import useMapSearchInputsToState from '../hooks/useMapSearchInputsToState.ts';
import useOnUpdateSearchInputs from '../hooks/useOnUpdateSearchInputs.ts';
import LeavingSecurityWarning from './LeavingSecurityWarning.tsx';
import RouteOptions from './RouteOptions.tsx';
import { InputsFooter, LevelInfo, directionsHeaderSpacing } from './styles.ts';

// `zone` is the layout height reserved for the pill beside a logo when collapsed; `expandedZone` places the pill 14px
// below the route toggle and 10px above the header bottom (Figma). The 44px hit area overlaps the rows above it.
const HEADER_PILL = { width: 44, height: 5, bottom: 10, hitArea: 44, zone: 25, expandedZone: 23.4 };
const LOGO_ROW_HEIGHT = 32;
const LOGO_ROW_GAP = 12;
const HEADER_PROGRESS = '--wf-header-progress';

// Reserve the measured preview/guidance sheet height on mobile, falling back to 130px before it mounts.
const WidgetWrapper = styled.div`
  ${({ $isMobile, $searchInputs }) =>
    !$isMobile &&
    $searchInputs.length > 5 &&
    css`
      @media (max-height: 700px) {
        max-height: 360px; // the height of 5 inputs plus add header and buttons
        overflow-y: auto;
        overflow-x: hidden;
      }
    `};
  width: 100%;
  padding: ${({ $isMobile, $hasLogoUrl, theme, $navInputsHidden, $showPillHandle }) =>
    $navInputsHidden && $isMobile && !$showPillHandle
      ? '4px 8px 0 8px'
      : getHeaderPadding($isMobile, $hasLogoUrl, theme)};
  ${({ $isMobile }) => $isMobile && `padding-bottom: 0px;`};
  ${({ $showPillHandle }) => $showPillHandle && directionsHeaderSpacing};
  ${({ $isMobile, $navInputsHidden, $hasLogoUrl, $showPillHandle }) =>
    $isMobile &&
    !$hasLogoUrl &&
    css`
      margin-top: ${
        $showPillHandle
          ? `calc(-${LOGO_ROW_HEIGHT}px * (1 - var(${HEADER_PROGRESS})))`
          : `${$navInputsHidden ? -LOGO_ROW_HEIGHT : 0}px`
      };
    `};
  ${({ $isMobile, $showPillHandle, $isHeaderDragging }) =>
    $isMobile &&
    $showPillHandle &&
    css`
      transition: ${$isHeaderDragging ? 'none' : 'margin-top 0.3s ease-in-out'};
      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }
    `};
  ${({ $isMobile }) =>
    $isMobile &&
    css`
      --wf-bottom-bar-height: var(--wf-bottom-bar-height-current, 130px);
      max-height: calc(100vh - var(--wf-bottom-bar-height));
      overflow-y: auto;
      overflow-x: hidden;
      -webkit-overflow-scrolling: touch;
      display: flex;
      flex-direction: column;
    `};
`;

const LogoRow = styled.div<{ $animated: boolean; $dragging: boolean }>`
  display: ${({ $animated }) => ($animated ? 'flex' : 'contents')};
  flex-direction: column;
  flex: none;
  ${({ $animated, $dragging }) =>
    $animated &&
    css`
      > div {
        min-height: ${LOGO_ROW_HEIGHT}px;
        margin-bottom: calc(${LOGO_ROW_GAP}px * var(${HEADER_PROGRESS}));
        transition: ${$dragging ? 'none' : 'margin-bottom 0.3s ease-in-out'};
        @media (prefers-reduced-motion: reduce) {
          transition: none;
        }
      }
    `};
`;

// Only this progress animates, so a changed height cap (viewport or bottom sheet) resizes the body without replaying the toggle transition.
const BODY_PROGRESS = '--wf-header-body-progress';
try {
  CSS.registerProperty({ name: BODY_PROGRESS, syntax: '<number>', inherits: false, initialValue: '1' });
} catch {
  // Unsupported or already registered: the body then settles without animating.
}

const HeaderBody = styled.div<{
  $animated: boolean;
  $height: number | null;
  $hidden: boolean;
  $dragging: boolean;
  $scrollable: boolean;
}>`
  display: ${({ $animated }) => ($animated ? 'block' : 'contents')};
  ${({ $animated, $height, $hidden, $dragging, $scrollable }) =>
    $animated &&
    css`
      flex: none;
      height: ${$height === null ? 'auto' : `calc(${$height}px * var(${BODY_PROGRESS}))`};
      /* Scroll only when the content exceeds the cap; while the height animates the content overflows momentarily and must not show a scrollbar. */
      overflow-y: ${$scrollable && !$hidden && !$dragging ? 'auto' : 'hidden'};
      /* Cover the wrapper's inline padding so controls hanging into it (e.g. swap) fit the scroll box instead of adding a horizontal scrollbar. */
      margin-inline: calc(-1 * var(--wf-header-inset-start, 0px)) calc(-1 * var(--wf-header-inset-end, 0px));
      padding-inline: var(--wf-header-inset-start, 0px) var(--wf-header-inset-end, 0px);
      visibility: ${$hidden && !$dragging ? 'hidden' : 'visible'};
      transition: ${$dragging ? 'none' : `${BODY_PROGRESS} 0.3s ease-in-out, visibility 0s ${$hidden ? '0.3s' : '0s'}`};
      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }
    `};
`;

const HeaderBodyContent = styled.div<{ $animated: boolean }>`
  display: ${({ $animated }) => ($animated ? 'flow-root' : 'contents')};
`;
const LeavingSecurityWarningWrapper = styled.div`
  margin-top: 5px;
`;

const InputAndAddWrapper = styled.div`
  ${({ $isMobile }) => {
    return $isMobile
      ? css`
          width: 100%;
        `
      : null;
  }}
`;

const RouteTypeSwitcherWrapper = styled.div`
  width: 100%;
  padding: 0;
`;

const ChevronDiv = styled.div<{ $compact?: boolean; $animated: boolean; $dragging: boolean }>`
  display: flex;
  flex: none;
  justify-content: center;
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${({ $animated }) => ($animated ? 'transparent' : 'var(--color-widgetBackground, #ffffff)')};
  /* The strip overlaps non-interactive rows without shrinking the tap target; only the button takes pointer events. */
  margin-block-start: ${({ $animated, $compact }) =>
    $animated
      ? css`calc(
          ${$compact ? HEADER_PILL.zone : HEADER_PILL.hitArea}px * (1 - var(${HEADER_PROGRESS})) +
            ${HEADER_PILL.expandedZone}px * var(${HEADER_PROGRESS}) - ${HEADER_PILL.hitArea}px
        )`
      : '0px'};
  ${({ $animated, $dragging }) =>
    $animated &&
    css`
      pointer-events: none;
      transition: ${$dragging ? 'none' : 'margin-block-start 0.3s ease-in-out'};
      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }
    `};
`;

const FlippingButton = styled(Button)`
  width: 100%;
  display: block;
  background-color: 'transparent';
  color: var(--color-mapNavText);
  padding: 0 12px;
  border: none;
  border-radius: unset;
  position: relative;
  transform: ${({ $isOpen }) => ($isOpen ? 'none' : 'rotate(180deg)')};

  &:focus-visible {
    outline-offset: -4px;
    border-radius: ${({ theme }) => theme.standardRadius};
  }
`;

const HeaderPillButton = styled.button`
  position: relative;
  flex: none;
  width: ${HEADER_PILL.hitArea}px;
  height: ${HEADER_PILL.hitArea}px;
  padding: 0;
  border: 0;
  border-radius: ${({ theme }) => theme.standardRadius};
  background: transparent;
  cursor: pointer;
  pointer-events: auto;
  touch-action: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  user-select: none;
  -webkit-user-select: none;

  &::after {
    position: absolute;
    inset-block-end: ${HEADER_PILL.bottom}px;
    inset-inline-start: 0;
    width: ${HEADER_PILL.width}px;
    height: ${HEADER_PILL.height}px;
    border-radius: 9999px;
    background-color: var(--color-secondary);
    content: '';
    opacity: 0.7;
  }

  &:focus-visible {
    outline-offset: -4px;
  }
`;

const SearchInputsAndSwap = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0; /* Allow flex children to shrink below content size */
`;

const SearchInputs = styled.div`
  flex: 1; /* Take up remaining space */
  min-width: 0; /* Allow shrinking below content size */
`;

const SwapWrapper = styled.div`
  flex-shrink: 0; /* Don't shrink */
  width: 32px; /* Fixed width to match icon */
`;

const ReverseDirectionsWrapper = styled.div`
  cursor: pointer;
`;

const ReverseDirectionsButton = styled.button`
  color: var(--color-widgetText);
  border: none;
  background: none;
  font-size: 12px !important;
`;

// disable all react-beautiful-dnd development warnings
window['__react-beautiful-dnd-disable-dev-warnings'] = false;

const ChevronButton = ({
  onClick,
  ariaLabel,
  navInputsHidden,
  chevronButtonRef,
  showPillHandle,
  hasLogo,
  dragging,
  pillHandlers,
}) => {
  const colors = useCSSColors();
  return (
    <ChevronDiv $compact={showPillHandle && hasLogo} $animated={showPillHandle} $dragging={dragging}>
      {showPillHandle ? (
        <HeaderPillButton
          type="button"
          data-testid="CloseInputChevronButton"
          {...pillHandlers}
          aria-label={ariaLabel}
          aria-expanded={!navInputsHidden}
          ref={chevronButtonRef}
        />
      ) : (
        <FlippingButton
          data-testid="CloseInputChevronButton"
          onClick={onClick}
          aria-label={ariaLabel}
          $isOpen={!navInputsHidden}
          aria-expanded={!navInputsHidden}
          ref={chevronButtonRef}
        >
          <Icon name="chevron-up" cdn fillColor={colors.secondary} />
        </FlippingButton>
      )}
    </ChevronDiv>
  );
};

const DirectionsResultControlsViewMulti = ({
  showLevelName,
  currentLevelName,
  routeAccessibiltyChoice,
  routeTypes,
  onSwitchRouteTypeButtonClicked,
  onBackButtonClicked,
  logoUrl,
  clientName,
  onInputChange,
  onInputClearButtonClick,
  onInputSelect,
  handleKeyPress,
  T,
  // new
  searchInputs,
  currentInputIndex,
  isMobile,
  showPillHandle = false,
  fullRoutePreviewEnabled = false,
  mobileHighZoomBreakpoint,
  handleSearchInputsChange,
  handleAddStopSelect,
  widgetState,
  overviewBottomBarRef,
}) => {
  const [internalSearchInputs, setInternalSearchInputs] = useState(searchInputs);
  const [draggingSearchInputs, setDraggingSearchInputs] = useState(searchInputs);
  const [isDragging, setIsDragging] = useState(false);
  const [navInputsHidden, setNavInputsHidden] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const logoRowRef = useRef<HTMLDivElement>(null);
  const headerBodyRef = useRef<HTMLDivElement>(null);
  const headerBodyContentRef = useRef<HTMLDivElement>(null);
  const [headerBodyHeight, setHeaderBodyHeight] = useState<number | null>(null);
  const [headerBodyScrollable, setHeaderBodyScrollable] = useState(false);
  const [bottomBar, setBottomBar] = useState<Element | null>(null);
  const measureHeaderBody = useRef(() => {});
  const animateHeader = isMobile && showPillHandle;

  // Register sensors explicitly so the keyboard sensor uses dnd-kit's sortable
  // coordinate getter. Without it, arrow keys nudge the dragged item by a fixed
  // 25px instead of hopping to the next sortable item, so a single ArrowDown may
  // not cross into the neighbour's droppable region (height-dependent flake) and
  // keyboard reordering effectively breaks for accessibility users.
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const colors = useCSSColors();
  const theme = useTheme(); // Get theme from styled-components context (replaces withTheme HOC)
  const hasLogo = Boolean(logoUrl && theme?.header?.hasLogo);

  useLayoutEffect(() => {
    if (!animateHeader) {
      return;
    }
    const wrapper = wrapperRef.current;
    const content = headerBodyContentRef.current;
    const logoRow = logoRowRef.current?.firstElementChild;
    if (!wrapper || !content || !logoRow) {
      return;
    }
    const measure = () => {
      const style = getComputedStyle(wrapper);
      wrapper.style.setProperty('--wf-header-inset-start', style.paddingInlineStart);
      wrapper.style.setProperty('--wf-header-inset-end', style.paddingInlineEnd);
      const chromeHeight =
        Number.parseFloat(style.paddingTop) +
        logoRow.getBoundingClientRect().height +
        LOGO_ROW_GAP +
        HEADER_PILL.expandedZone;
      const availableHeight = Math.max(0, Number.parseFloat(style.maxHeight) - chromeHeight);
      const contentHeight = content.getBoundingClientRect().height;
      setHeaderBodyHeight(Math.min(contentHeight, availableHeight));
      setHeaderBodyScrollable(contentHeight > availableHeight + 1);
    };
    measureHeaderBody.current = measure;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    observer.observe(wrapper);
    observer.observe(logoRow);
    window.addEventListener('resize', measure);
    return () => {
      measureHeaderBody.current = () => {};
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [animateHeader, theme]);

  // The ref can switch between preview and guidance nodes without changing its identity.
  useEffect(() => {
    const content = overviewBottomBarRef?.current;
    const nextBottomBar = isMobile
      ? (content?.closest('.DirectionsResultSheet') ??
        content ??
        document.querySelector('[data-testid="DirectionsResultViewMobile"]'))
      : null;
    if (nextBottomBar !== bottomBar) {
      setBottomBar(nextBottomBar);
    }
  });

  // Measure bottom bar (mobile directions result view) height and store in CSS variable for dynamic resizing
  useEffect(() => {
    if (!isMobile) {
      return;
    }
    const updateBottomBarHeight = () => {
      const wrapper = wrapperRef.current;
      // The full-height preview inerts and covers this header, and a sheet mid-transition has no settled height: keep the last reserve.
      if (
        animateHeader &&
        (wrapper?.closest('[inert]') ||
          bottomBar?.getAnimations?.().some(animation => (animation as CSSTransition).transitionProperty === 'height'))
      ) {
        return;
      }
      if (bottomBar && wrapper) {
        const rect = bottomBar.getBoundingClientRect();
        // Only set if the bottom bar is visible within viewport and has height
        if (rect.height > 0) {
          wrapper.style.setProperty('--wf-bottom-bar-height-current', `${Math.round(rect.height)}px`);
          measureHeaderBody.current();
          return;
        }
      }
      wrapper?.style.removeProperty('--wf-bottom-bar-height-current');
      measureHeaderBody.current();
    };
    updateBottomBarHeight();
    window.addEventListener('resize', updateBottomBarHeight);
    bottomBar?.addEventListener('transitionend', updateBottomBarHeight);
    const resizeObserver = window.ResizeObserver ? new ResizeObserver(updateBottomBarHeight) : null;
    if (resizeObserver) {
      if (bottomBar) {
        resizeObserver.observe(bottomBar);
      }
    }
    return () => {
      window.removeEventListener('resize', updateBottomBarHeight);
      bottomBar?.removeEventListener('transitionend', updateBottomBarHeight);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [isMobile, animateHeader, bottomBar]);

  useEffect(() => {
    // If the navInputs are hidden and the screen size is in desktop view, show the navInputs
    if (navInputsHidden && !isMobile) {
      setNavInputsHidden(false);
    }
  }, [isMobile]);

  // Incoming app state
  useMapSearchInputsToState(currentInputIndex, searchInputs, setInternalSearchInputs, setDraggingSearchInputs);

  // Outgoing update to app state
  useOnUpdateSearchInputs(searchInputs, internalSearchInputs, handleSearchInputsChange);

  const chevronButtonRef = useRef(null);
  const routeTypeAccessibleToggleRef = useRef(null);
  const shouldHideRouteOptions = fullRoutePreviewEnabled && !widgetState.getState().isNavigating;

  const modalCloseToggleRef = useRef(null);

  useEffect(() => {
    if (widgetState && typeof widgetState.update === 'function') {
      widgetState.update({ modalCloseToggleRef });
    }
  }, [widgetState]);

  // Let the route overview measure how much of the map the header covers
  useEffect(() => {
    if (widgetState && typeof widgetState.update === 'function') {
      widgetState.update({ overviewHeaderRef: wrapperRef });
    }
  }, [widgetState]);

  // Persist the ref into widgetState so other modules (e.g., toast notifications) can refocus it after dismiss
  useEffect(() => {
    if (widgetState && typeof widgetState.update === 'function') {
      widgetState.update({ routeTypeAccessibleToggleRef });
    }
  }, [widgetState]);

  const setHeaderOpen = (isOpen: boolean) => {
    setNavInputsHidden(!isOpen);
    setTimeout(() => {
      // This is hacky. If we don't use this and change how the Inputs autofocus works, multiple things break
      if (chevronButtonRef.current) {
        chevronButtonRef.current.focus();
      }
    }, 0);
  };

  const [dragProgress, setDragProgress] = useState<number | null>(null);
  const dragStart = useRef({ progress: 0, range: 1 });
  const pillHandlers = usePillDrag({
    isOpen: !navInputsHidden,
    onOpenChange: setHeaderOpen,
    openDirection: 'down',
    enabled: animateHeader,
    onDragStart: () => {
      const progress =
        headerBodyHeight !== null && headerBodyHeight > 0
          ? (headerBodyRef.current?.getBoundingClientRect().height ?? 0) / headerBodyHeight
          : Number.parseFloat(getComputedStyle(logoRowRef.current!.firstElementChild!).marginBottom) / LOGO_ROW_GAP;
      dragStart.current = {
        progress,
        range:
          (headerBodyHeight ?? 0) +
          LOGO_ROW_GAP +
          (HEADER_PILL.expandedZone - (hasLogo ? HEADER_PILL.zone : HEADER_PILL.hitArea)) +
          (typeof logoUrl === 'undefined' ? LOGO_ROW_HEIGHT : 0),
      };
      setDragProgress(progress);
    },
    onDragMove: distance => {
      const { progress, range } = dragStart.current;
      setDragProgress(Math.max(0, Math.min(1, progress + distance / range)));
    },
    onDragEnd: () => setDragProgress(null),
  });
  const headerProgress = dragProgress ?? (navInputsHidden ? 0 : 1);
  const isHeaderDragging = dragProgress !== null;

  return (
    <WidgetWrapper
      ref={wrapperRef}
      data-testid={'DirectionsResultControlsViewMulti'}
      data-mobile-directions-pill={animateHeader || undefined}
      $hasLogoUrl={typeof logoUrl !== 'undefined'}
      $isMobile={isMobile}
      $navInputsHidden={navInputsHidden}
      $showPillHandle={showPillHandle}
      style={{ [HEADER_PROGRESS]: headerProgress } as React.CSSProperties}
      $isHeaderDragging={isHeaderDragging}
      theme={theme}
      $searchInputs={searchInputs}
    >
      <LogoRow ref={logoRowRef} $animated={animateHeader} $dragging={isHeaderDragging}>
        <LogoAndCloseButton
          logoUrl={logoUrl}
          theme={theme}
          isMobile={isMobile}
          clientName={clientName}
          onClose={onBackButtonClicked}
          T={T}
          hideClose={navInputsHidden && isMobile}
        />
      </LogoRow>
      <HeaderBody
        ref={headerBodyRef}
        $animated={animateHeader}
        $height={headerBodyHeight}
        $hidden={navInputsHidden}
        $dragging={isHeaderDragging}
        $scrollable={headerBodyScrollable}
        style={{ [BODY_PROGRESS]: headerProgress } as React.CSSProperties}
        inert={animateHeader && navInputsHidden}
        aria-hidden={animateHeader && navInputsHidden ? true : undefined}
        data-testid="DirectionsHeaderBody"
      >
        <HeaderBodyContent ref={headerBodyContentRef} $animated={animateHeader}>
          {(animateHeader || !navInputsHidden) && (
            <InputAndAddWrapper $isMobile={isMobile}>
              <DndContext
                sensors={sensors}
                modifiers={[restrictToVerticalAxis]}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart(internalSearchInputs, setDraggingSearchInputs, setIsDragging)}
                onDragOver={handleDragOver(internalSearchInputs, setDraggingSearchInputs)}
                onDragEnd={handleDragEnd(setInternalSearchInputs, setIsDragging)}
                onDragCancel={() => setIsDragging(false)}
              >
                <SortableContext items={internalSearchInputs} strategy={verticalListSortingStrategy}>
                  <SearchInputsAndSwap>
                    <SearchInputs>
                      {internalSearchInputs.map((input, index) => {
                        let placeholder = `${T('Add stop')}:`;
                        if (index === 0) {
                          placeholder = `${T('From')}:`;
                        }
                        if (index === internalSearchInputs.length - 1) {
                          placeholder = `${T('To')}:`;
                        }
                        const first = isDragging ? draggingSearchInputs[0].id === input.id : index === 0;
                        const last = isDragging
                          ? draggingSearchInputs[draggingSearchInputs.length - 1].id === input.id
                          : index === internalSearchInputs.length - 1;
                        return (
                          <SearchInputLabelledMulti
                            data-testid={'SearchInputLabelledMulti'}
                            index={index}
                            stopNumber={
                              isDragging ? draggingSearchInputs.findIndex(item => item.id === input.id) : index
                            }
                            searchInputs={searchInputs}
                            id={input.id} // Both this prop and 'key' prop seem to need to have the same value
                            key={input.id || index}
                            autoFocus={
                              !navInputsHidden && !widgetState.getState().modalIsShowing && currentInputIndex === index
                            }
                            value={input.term ? input.term : ''}
                            placeholder={placeholder}
                            onInputChange={onInputChange}
                            onInputSelect={onInputSelect}
                            onClick={() => onInputSelect(index)}
                            onInputClearButtonClick={handleClearButtonClick(
                              internalSearchInputs,
                              setInternalSearchInputs,
                              onInputClearButtonClick,
                              index,
                            )}
                            clickableItemOptions={!(internalSearchInputs.length < 3)}
                            handleRemoveClick={handleRemoveClick(
                              internalSearchInputs,
                              setInternalSearchInputs,
                              index,
                              widgetState,
                            )}
                            handleMoveClick={handleMoveClick(
                              internalSearchInputs,
                              setInternalSearchInputs,
                              index,
                              widgetState,
                            )}
                            handleKeyPress={handleKeyPress}
                            draggable={true}
                            first={first}
                            last={last}
                            T={T}
                            isMobile={isMobile}
                            mobileHighZoomBreakpoint={mobileHighZoomBreakpoint}
                          />
                        );
                      })}
                    </SearchInputs>
                    {internalSearchInputs.length <= 2 && (
                      <SwapWrapper>
                        <Tooltip
                          text={T('getDirectionsFromTo:Swap start and end points around')}
                          arrowPosition="left"
                          isVisible={internalSearchInputs?.length < 3}
                        >
                          <ReverseDirectionsWrapper $internalSearchInputs={internalSearchInputs}>
                            <ReverseDirectionsButton
                              data-testid="ReverseDirectionsButton"
                              onClick={reverseDirections(internalSearchInputs, setInternalSearchInputs)}
                              aria-label={T('getDirectionsFromTo:Swap start and end points around')}
                            >
                              <Icon
                                className="icon"
                                width={32}
                                height={32}
                                name="swap"
                                cdn
                                fillColor={colors.widgetIconFill}
                              />
                            </ReverseDirectionsButton>
                          </ReverseDirectionsWrapper>
                        </Tooltip>
                      </SwapWrapper>
                    )}
                  </SearchInputsAndSwap>
                </SortableContext>
              </DndContext>

              <InputsFooter>
                <AddWaypointButton
                  ref={modalCloseToggleRef}
                  text={T('getDirectionsFromTo:Maximum Stops Reached')}
                  disabled={internalSearchInputs.length > MAX_SEARCH_INPUT_LENGTH - 1}
                  T={T}
                  theme={theme}
                  handleAddStop={handleAddStop(internalSearchInputs, setInternalSearchInputs, handleAddStopSelect)}
                />
                <WheelchairAccessibilityToggle
                  ref={routeTypeAccessibleToggleRef}
                  routeTypes={routeTypes}
                  routeAccessibiltyChoice={routeAccessibiltyChoice}
                  onSwitchRouteTypeButtonClicked={onSwitchRouteTypeButtonClicked}
                  T={T}
                  theme={theme}
                />
              </InputsFooter>
            </InputAndAddWrapper>
          )}
          {!shouldHideRouteOptions &&
            ((animateHeader || !navInputsHidden) && isMobile ? (
              <RouteTypeSwitcherWrapper>
                <RouteOptions
                  routeAccessibiltyChoice={routeAccessibiltyChoice}
                  routeTypes={routeTypes}
                  onSwitchRouteTypeButtonClicked={onSwitchRouteTypeButtonClicked}
                  T={T}
                />
              </RouteTypeSwitcherWrapper>
            ) : animateHeader || !navInputsHidden ? (
              <RouteOptions
                routeAccessibiltyChoice={routeAccessibiltyChoice}
                routeTypes={routeTypes}
                onSwitchRouteTypeButtonClicked={onSwitchRouteTypeButtonClicked}
                T={T}
              />
            ) : null)}
          {!shouldHideRouteOptions && (animateHeader || !navInputsHidden) && widgetState.getState().leavingSecurity && (
            <LeavingSecurityWarningWrapper>
              <LeavingSecurityWarning T={T} theme={theme}></LeavingSecurityWarning>
            </LeavingSecurityWarningWrapper>
          )}
        </HeaderBodyContent>
      </HeaderBody>

      {isMobile && (
        <ChevronButton
          data-testid="ChevronButton"
          ariaLabel={T('ui:Toggle content')}
          onClick={() => setHeaderOpen(navInputsHidden)}
          navInputsHidden={navInputsHidden}
          chevronButtonRef={chevronButtonRef}
          showPillHandle={showPillHandle}
          hasLogo={hasLogo}
          dragging={isHeaderDragging}
          pillHandlers={pillHandlers}
        ></ChevronButton>
      )}
      {showLevelName && (
        <LevelInfo>
          <span>{currentLevelName}</span>
        </LevelInfo>
      )}
    </WidgetWrapper>
  );
};

export default DirectionsResultControlsViewMulti;

DirectionsResultControlsViewMulti.propTypes = {
  routeAccessibiltyChoice: PropTypes.string,
  routeTypes: PropTypes.string,
  isNavigating: PropTypes.bool,
  showPillHandle: PropTypes.bool,
  fullRoutePreviewEnabled: PropTypes.bool,
  time: PropTypes.number,
  distance: PropTypes.number,
  currentLevelName: PropTypes.string,
  showLevelName: PropTypes.bool,
  mobileHighZoomBreakpoint: PropTypes.number,
};
